import { useCallback, useEffect, useState } from 'react';

import LocalStorage from '~/state/localStorage';

/**
 * One-off feature intro: `shouldShow` turns true once, after the persisted flag has been read
 * and only if this `key` has not been seen at `version` or later. `dismiss` closes it and
 * records it, so it never auto-opens again on this device.
 *
 * Bump `version` to re-introduce a feature whose intro has been materially rewritten.
 *
 * Nothing here blocks the screen: the flag read is async and the intro simply appears when it
 * resolves. Callers that also expose a manual entry point (an info icon) should keep their own
 * open state and use this purely for the automatic first run — see AIStrategiesList.
 */
export const useOnboarding = (key: string, version = 1) => {
    const [shouldShow, setShouldShow] = useState(false);

    useEffect(() => {
        let active = true;
        LocalStorage.onboarding
            .get()
            .then((seen) => {
                if (active && (seen?.[key] ?? 0) < version) {
                    setShouldShow(true);
                }
            })
            .catch(() => {
                // Storage unavailable: skip the intro rather than showing it on every launch.
            });
        return () => {
            active = false;
        };
    }, [key, version]);

    const dismiss = useCallback(async () => {
        setShouldShow(false);
        // Read-modify-write of the whole map. Intros are opened one at a time by a person, so
        // there is no concurrent writer to lose.
        const seen = (await LocalStorage.onboarding.get()) ?? {};
        await LocalStorage.onboarding.set({ ...seen, [key]: version });
    }, [key, version]);

    return { shouldShow, dismiss };
};
